import { useState } from "react";
import { useParams, Link } from "wouter";
import { useGetProduct } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  Loader2, CreditCard, Wallet, ArrowLeft,
  ExternalLink, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useWallet } from "@/hooks/useWallet";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useToast } from "@/hooks/use-toast";

// Per-product image galleries (ordered)
const PRODUCT_GALLERIES: Record<number, string[]> = {
  35: ["/illz-queens-jungle-graphic.png"],
  36: [
    "/illz-new-world-city-sunset.png",
    "/illz-new-world-city-dusk.jpg",
    "/illz-new-world-city-morning.png",
    "/illz-new-world-city-day.png",
  ],
  37: [
    "/farrah-artist-cover.jpeg",
    "/farrah-artist-brush.jpeg",
    "/farrah-artist-easel.jpeg",
    "/farrah-artist-irl.jpeg",
    "/farrah-artist-print.jpeg",
  ],
  38: [
    "/cuzo-movie-poster.jpg",
    "/cuzo-menu-overview.png",
    "/cuzo-menu-et-v1.png",
    "/cuzo-promo-et.png",
  ],
  39: [
    "/jr-sticker-collection.png",
    "/jr-sticker-redhead.jpg",
    "/jr-sticker-rogue.jpg",
    "/jr-sticker-spider-girl.jpg",
    "/jr-sticker-blonde-blue.png",
    "/jr-sticker-catwoman.jpg",
  ],
  40: [
    "/jr-jean-phoenix-look-3.jpg",
    "/jr-sticker-collection.png",
    "/jr-jean-phoenix-portrait.jpg",
    "/jr-sticker-catwoman.jpg",
  ],
  41: [
    "/jr-training-day-claudia.png",
    "/jr-jean-phoenix-look-3.jpg",
    "/jr-sticker-collection.png",
    "/jr-sticker-catwoman.jpg",
  ],
  42: [
    "/jr-training-day-claudia.png",
    "/jr-jean-phoenix-look-3.jpg",
    "/jr-sticker-collection.png",
  ],
  43: [
    "/balcony-between-worlds/merch/cover.jpg",
    "/balcony-between-worlds/merch/miami-balcony.jpg",
    "/balcony-between-worlds/merch/portal-battle.png",
    "/balcony-between-worlds/merch/balcony-attack.png",
    "/balcony-between-worlds/merch/emerald-onyx-arrival.png",
    "/balcony-between-worlds/merch/emerald-onyx-battle.png",
  ],
  44: [
    "/balcony-between-worlds/merch/emerald-onyx-arrival.png",
    "/balcony-between-worlds/merch/emerald-onyx-battle.png",
    "/balcony-between-worlds/merch/portal-battle.png",
    "/balcony-between-worlds/merch/balcony-attack.png",
    "/balcony-between-worlds/merch/miami-balcony.jpg",
    "/balcony-between-worlds/merch/cover.jpg",
  ],
};

const TRAINING_DAYS_PRODUCTS = [
  { id: 39, label: "Sticker character set", range: "$25", includes: "Sticker character set" },
  { id: 40, label: "ISM art / poster pack", range: "$75–$150", includes: "Collaboration artwork, poster, and stickers" },
  { id: 41, label: "Medium art package", range: "$225–$500", includes: "Medium art, stickers, and 1 of 1,000 first issue" },
  { id: 42, label: "Custom large artwork", range: "$2,000–$3,800", includes: "Large custom art, Issues 1–5, and exclusive series access" },
] as const;

const TRAINING_DAYS_PROMO_SRC =
  "https://www.facebook.com/plugins/video.php?height=315&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1471539194902449%2F&show_text=false&width=560&t=0";
const STICKERS_ONLY_URL = "https://www.stickermule.com/joerussketchbook";

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const { data: product, isLoading, isError } = useGetProduct(productId);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const { isConnected } = useWallet();
  const { startCheckout, startContentCheckout, isLoading: checkoutLoading } = useStripeCheckout();
  const { toast } = useToast();

  const gallery = PRODUCT_GALLERIES[productId] ?? (product ? [product.imageUrl] : []);

  const handleBuy = (amountUsdOverride?: number, nameOverride?: string) => {
    if (!product) return;
    const usdAmount = amountUsdOverride ?? ((product as any).priceUsd as number | null | undefined);
    const productName = nameOverride ?? product.name;
    const productType = (product as any).productType ?? "digital";
    if (productType === "content_access" && (product.id === 43 || product.id === 44)) {
      startContentCheckout(product.id);
      return;
    }
    // Always use Stripe for USD-priced items — even if a wallet is connected,
    // card/Apple Pay/Google Pay should still be available.
    if (usdAmount && usdAmount > 0) {
      startCheckout({
        productName,
        productType,
        amountUsd: usdAmount,
        metadata: { productId: String(product.id), blockchain: product.blockchain },
      });
    } else if (isConnected) {
      toast({
        title: "Transaction Initiated",
        description: `Approve the transaction in your wallet to purchase ${product.name}.`,
      });
    } else {
      toast({
        title: "Wallet Required",
        description: `${product.name} is a crypto-only asset. Connect your wallet to purchase.`,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground text-lg">Product not found.</p>
        <Link href="/marketplace">
          <Button variant="outline" className="font-mono uppercase tracking-widest">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Marketplace
          </Button>
        </Link>
      </div>
    );
  }

  const desc = (product as any).description as string | undefined;
  const hasBread = product?.id === 37;
  const isEvent = (product as any).productType === "event";
  const priceUsd = (product as any).priceUsd as number | undefined;
  const likes = (product as any).likes as number | undefined;
  const views = (product as any).views as number | undefined;
  const tokenId = (product as any).tokenId as string | undefined;
  const verified = (product as any).verified as boolean | undefined;
  const isTrainingDays = product.id >= 39 && product.id <= 42;

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      {/* Back nav */}
      <div className="container mx-auto px-4 pt-6 pb-2">
        <Link href="/marketplace">
          <button className="flex items-center gap-2 text-muted-foreground hover:text-white text-sm font-mono mb-6 transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Marketplace
          </button>
        </Link>
      </div>

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">

          {/* ─── Gallery ─── */}
          <div>
            {/* Main image */}
            <div className="relative aspect-square bg-black/60 rounded-2xl overflow-hidden border border-border mb-3">
              <img
                src={gallery[galleryIdx]}
                alt={`${product.name} — image ${galleryIdx + 1}`}
                className="w-full h-full object-cover transition-opacity duration-300"
              />
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIdx(i => (i - 1 + gallery.length) % gallery.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/90 p-2 rounded-full border border-white/10 backdrop-blur-sm transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setGalleryIdx(i => (i + 1) % gallery.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/90 p-2 rounded-full border border-white/10 backdrop-blur-sm transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  {/* Dot indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {gallery.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setGalleryIdx(i)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          i === galleryIdx ? "bg-white scale-125" : "bg-white/30 hover:bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
              {/* Image count badge */}
              {gallery.length > 1 && (
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md px-2 py-1 rounded text-xs font-mono border border-white/10 text-muted-foreground">
                  {galleryIdx + 1} / {gallery.length}
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setGalleryIdx(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      i === galleryIdx
                        ? "border-primary ring-2 ring-primary/30"
                        : "border-transparent hover:border-white/30 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ─── Details panel ─── */}
          <div className="flex flex-col gap-6">

            {/* Category / chain badges */}
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-mono border border-white/10 bg-black/40 uppercase text-muted-foreground">
                {product.category}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-mono border border-white/10 bg-black/40 text-primary font-bold">
                {product.blockchain}
              </span>
              {verified && (
                <span className="px-3 py-1 rounded-full text-xs font-mono border border-primary/40 bg-primary/10 text-primary">
                  ✓ Verified
                </span>
              )}
            </div>

            {/* Title + seller */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-2">
                {product.name}
              </h1>
              <p className="text-muted-foreground text-sm">
                By <span className="text-white font-semibold">{product.sellerName}</span>
              </p>
            </div>

            {/* Stats row */}
            {!!(likes || views) && (
              <div className="flex gap-6 text-sm text-muted-foreground">
                {!!likes && <span>❤ {likes.toLocaleString()} likes</span>}
                {!!views && <span>👁 {views.toLocaleString()} views</span>}
              </div>
            )}

            {/* Description */}
            {desc && (
              <div className="bg-card/40 rounded-xl p-5 border border-border">
                <h3 className="text-xs font-mono uppercase text-muted-foreground mb-3 tracking-widest">
                  {isEvent ? "Event Details" : "About This Piece"}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{desc}</p>
                {product.id === 39 && (
                  <a
                    href={STICKERS_ONLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-primary/80 mt-5 w-full sm:w-auto"
                  >
                    Purchase stickers only
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {hasBread && (
                  <a
                    href="https://breadandbuttamag.com/features/feat-farrah-001"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-primary text-sm font-mono mt-4 hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    BREAD Magazine Feature
                  </a>
                )}
              </div>
            )}

            {isTrainingDays && (
              <div className="bg-card/40 rounded-xl p-5 border border-primary/30">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-xs font-mono uppercase text-primary mb-1 tracking-widest">
                      Training Days · Promo
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Joe Rus Sketch Book × Claudia G. · Blood Eclipse / Refs of ILL
                    </p>
                  </div>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground border border-white/10 rounded px-2 py-1 whitespace-nowrap">
                    Series preview
                  </span>
                </div>
                <div className="aspect-video w-full overflow-hidden rounded-lg border border-white/10 bg-black">
                  <iframe
                    src={TRAINING_DAYS_PROMO_SRC}
                    title="Training Days series promo"
                    className="w-full h-full"
                    style={{ border: "none", overflow: "hidden" }}
                    scrolling="no"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {product.id === 39 && (
              <div className="bg-card/40 rounded-xl p-5 border border-border">
                <h3 className="text-xs font-mono uppercase text-muted-foreground mb-4 tracking-widest">
                  Choose your package
                </h3>
                <div className="space-y-2">
                  {TRAINING_DAYS_PRODUCTS.map((pkg) => (
                    <Link key={pkg.id} href={`/marketplace/${pkg.id}`}>
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-3 hover:border-primary/50 transition-colors cursor-pointer">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white">{pkg.label}</p>
                          <p className="text-xs text-muted-foreground truncate">{pkg.includes}</p>
                        </div>
                        <span className="text-primary font-mono text-sm whitespace-nowrap">{pkg.range}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Purchase card */}
            {isEvent ? (
              <div className="bg-card/40 rounded-xl p-6 border border-border">
                <h3 className="text-xs font-mono uppercase text-muted-foreground mb-4 tracking-widest">
                  Ticket Options
                </h3>
                <div className="space-y-3">
                  {/* General Admission */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="font-bold text-white">General Admission</p>
                        <p className="text-xs text-muted-foreground">Includes snack &amp; drink · $20 at door</p>
                      </div>
                      <p className="font-mono font-bold text-xl text-primary">$15</p>
                    </div>
                    <Button
                      onClick={() => handleBuy(15, "General Admission — Cuzo Film Screening")}
                      disabled={checkoutLoading}
                      className="w-full bg-white text-black hover:bg-white/80 font-bold uppercase tracking-wider py-5 text-sm mt-3"
                    >
                      {checkoutLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><CreditCard className="w-4 h-4 mr-2" />Buy GA Ticket — $15</>
                      )}
                    </Button>
                  </div>
                  {/* Combo Pack */}
                  <div className="border border-primary/40 rounded-xl p-4 bg-primary/5">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="font-bold text-white">Combo Pack</p>
                        <p className="text-xs text-muted-foreground">Ticket + Full Meal + Dessert · $38 at event</p>
                      </div>
                      <p className="font-mono font-bold text-xl text-primary">$30</p>
                    </div>
                    <Button
                      onClick={() => handleBuy(30, "Combo Pack — Cuzo Film Screening")}
                      disabled={checkoutLoading}
                      className="w-full bg-primary text-black hover:bg-primary/80 font-bold uppercase tracking-wider py-5 text-sm mt-3"
                    >
                      {checkoutLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><CreditCard className="w-4 h-4 mr-2" />Buy Combo Pack — $30</>
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-center text-muted-foreground font-mono mt-4">
                  Secure card payment via Stripe · Event by Reel-ISM
                </p>
              </div>
            ) : (
              <div className="bg-card/40 rounded-xl p-6 border border-border mt-auto">
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                      Starting Price
                    </p>
                    <p className="font-mono font-bold text-3xl leading-none">
                      {product.price.toLocaleString()}{" "}
                      <span className="text-base text-muted-foreground">{product.currency}</span>
                    </p>
                    {priceUsd && (
                      <p className="text-sm text-muted-foreground mt-1">
                        ${priceUsd.toLocaleString()} USD
                      </p>
                    )}
                  </div>
                  {tokenId && (
                    <p className="text-xs text-muted-foreground font-mono">{tokenId}</p>
                  )}
                </div>

                <Button
                  onClick={() => handleBuy()}
                  disabled={checkoutLoading}
                  className="w-full bg-white text-black hover:bg-white/80 font-bold uppercase tracking-wider py-6 text-sm mb-3"
                >
                  {checkoutLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" />Purchase with Card</>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground font-mono">
                  Payments secured via Stripe · On-chain via {product.blockchain}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
